import { Component, inject, OnDestroy, signal } from '@angular/core';
import {
  GroupedResourceListItem,
  IAreaDetail,
  IPropertyFilter,
  IResourceFilterUpdateCrate,
} from '@outlook-addin/cue-http';
import { ResourceAssignType } from '@cue/api';
import { Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import * as fromModule from '../ngrx/reducers';

import { ResourcesActions } from '../ngrx/actions';
import { cueClose, cueFilter, IconComponent } from '@cue/ui/icons';
import { translation } from '@cue/translations';
import { loadResourceList } from '../ngrx/actions/resources.actions';

import { TranslocoPipe } from '@jsverse/transloco';
import { ResourceFilterComponent } from '../resource-filter/resource-filter.component';
import {
  ResourceListComponent,
  type ResourceNotification,
  type ResourceSelectionWarning,
} from '../resource-list/resource-list.component';
import { ResourceAvailabilityComponent } from '../resource-availability/resource-availability.component';
import { HierarchyArea, Pack } from '@cue/data';
import { AsyncPipe } from '@angular/common';
import { AddinConfigService } from '@outlook-addin/shared';

type ResourceSelectionWarningView = ResourceSelectionWarning & {
  countdownId: number;
  timeoutMs: number;
};

type ResourceNotificationView = ResourceNotification & {
  timeoutMs: number;
};

@Component({
  selector: 'addin-reservation-list',
  templateUrl: './reservation-list.component.html',
  styleUrls: ['./reservation-list.component.scss'],
  imports: [
    IconComponent,
    ResourceFilterComponent,
    ResourceListComponent,
    ResourceAvailabilityComponent,
    TranslocoPipe,
    AsyncPipe,
  ],
})
export class ReservationListComponent implements OnDestroy {
  translation = translation;
  resourceAssignType = ResourceAssignType;
  showFilter: boolean;
  resourceSelectionWarning = signal<ResourceSelectionWarningView | null>(null);
  resourceRemovalNotifications = signal<ResourceNotificationView[]>([]);
  resourcesFilter$: Observable<IPropertyFilter[]>;
  resourceList$: Observable<Pack<any[]>>;
  groupedResourceList$: Observable<Pack<GroupedResourceListItem[]>>;
  currentResourceFilter$: Observable<IResourceFilterUpdateCrate>;

  reqCount$: Observable<number>;

  areas$: Observable<IAreaDetail[]>;
  hierarchyAreas$: Observable<HierarchyArea[]>;
  protected readonly notificationTimeoutMs = 10000;
  private resourceSelectionWarningTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly resourceRemovalNotificationTimeouts = new Map<number, ReturnType<typeof setTimeout>>();
  private readonly subscriptionList = new Subscription();
  private readonly configService = inject(AddinConfigService);
  private currentNotificationRangeKey: string | null = null;
  private notificationCountdownId = 0;

  constructor(private store: Store<fromModule.State>) {
    const defaultLanguage =
      this.configService.value.configuration.languages.find((language) => language.default)?.code ?? 'en';
    this.store.dispatch(ResourcesActions.setDefaultLanguage({ defaultLanguage }));
    this.store.dispatch(ResourcesActions.loadAreas());
    this.store.dispatch(ResourcesActions.loadResourcesFilter());

    this.resourcesFilter$ = this.store.select(fromModule.selectResourcesFilter);
    this.resourceList$ = this.store.select(fromModule.selectResourceList);
    this.groupedResourceList$ = this.store.select(fromModule.selectGroupedResourceList);
    this.hierarchyAreas$ = this.store.select(fromModule.selectHierarchyAreas);
    this.areas$ = this.store.select(fromModule.selectAreas);
    this.currentResourceFilter$ = this.store.select(fromModule.selectCurrentResourceListFilter);
    this.reqCount$ = this.store.select(fromModule.selectRequirementsCount);
    this.subscriptionList.add(
      this.currentResourceFilter$.subscribe((filter) => this.handleCurrentResourceFilterChange(filter))
    );
  }

  resetAllFilters() {
    // this.showFilter = false;
    this.store.dispatch(ResourcesActions.resetRequirements());
  }

  protected readonly cueFilter = cueFilter;
  protected readonly cueClose = cueClose;

  onRefresh() {
    this.store.dispatch(loadResourceList());
  }

  setResourceSelectionWarning(warning: ResourceSelectionWarning | null) {
    this.clearResourceSelectionWarningTimeout();
    const timeoutMs = warning?.confirmation?.timeoutMs ?? this.notificationTimeoutMs;
    this.resourceSelectionWarning.set(
      warning ? { ...warning, countdownId: ++this.notificationCountdownId, timeoutMs } : null
    );

    if (warning) {
      this.resourceSelectionWarningTimeout = setTimeout(() => {
        if (warning.confirmation) {
          if (warning.confirmation.defaultAction === 'secondary') {
            this.cancelResourceSelectionWarning();
            return;
          }

          this.confirmResourceSelectionWarning();
          return;
        }

        this.closeResourceSelectionWarning();
      }, timeoutMs);
    }
  }

  closeResourceSelectionWarning() {
    this.clearResourceSelectionWarningTimeout();
    this.resourceSelectionWarning.set(null);
  }

  confirmResourceSelectionWarning() {
    const warning = this.resourceSelectionWarning();
    if (!warning) {
      return;
    }

    this.closeResourceSelectionWarning();
    warning.confirmation?.primaryAction();
  }

  cancelResourceSelectionWarning() {
    const warning = this.resourceSelectionWarning();
    if (!warning) {
      return;
    }

    this.closeResourceSelectionWarning();
    warning.confirmation?.secondaryAction();
  }

  showResourceRemovalNotification(notification: ResourceNotification) {
    const notificationKey = this.getResourceRemovalNotificationKey(notification);
    if (
      this.resourceRemovalNotifications().some(
        (existingNotification) => this.getResourceRemovalNotificationKey(existingNotification) === notificationKey
      )
    ) {
      return;
    }

    const notificationView: ResourceNotificationView = {
      ...notification,
      timeoutMs: notification.confirmation?.timeoutMs ?? this.notificationTimeoutMs,
    };

    this.resourceRemovalNotifications.update((notifications) => [notificationView, ...notifications]);

    const timeoutId = setTimeout(() => {
      if (notification.confirmation) {
        this.confirmResourceRemovalNotification(notification.id);
        return;
      }

      this.closeResourceRemovalNotification(notification.id);
    }, notificationView.timeoutMs);
    this.resourceRemovalNotificationTimeouts.set(notification.id, timeoutId);
  }

  private getResourceRemovalNotificationKey(notification: ResourceNotification): string {
    const resourceIds = notification.resourceDetails
      .map((detail) => detail.resource.resourceId)
      .sort((a, b) => a - b)
      .join(',');

    return [
      notification.messageKey,
      resourceIds,
      notification.start?.toISOString(),
      notification.end?.toISOString(),
    ].join('|');
  }

  closeResourceRemovalNotification(notificationId: number) {
    const timeoutId = this.resourceRemovalNotificationTimeouts.get(notificationId);
    if (timeoutId) {
      clearTimeout(timeoutId);
      this.resourceRemovalNotificationTimeouts.delete(notificationId);
    }

    this.resourceRemovalNotifications.update((notifications) =>
      notifications.filter((notification) => notification.id !== notificationId)
    );
  }

  confirmResourceRemovalNotification(notificationId: number) {
    const notification = this.resourceRemovalNotifications().find((item) => item.id === notificationId);
    if (!notification) {
      return;
    }

    this.closeResourceRemovalNotification(notificationId);
    notification.confirmation?.primaryAction();
  }

  cancelResourceRemovalNotification(notificationId: number) {
    const notification = this.resourceRemovalNotifications().find((item) => item.id === notificationId);
    if (!notification) {
      return;
    }

    this.closeResourceRemovalNotification(notificationId);
    notification.confirmation?.secondaryAction();
  }

  ngOnDestroy(): void {
    this.clearResourceSelectionWarningTimeout();
    this.resourceRemovalNotificationTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
    this.resourceRemovalNotificationTimeouts.clear();
    this.subscriptionList.unsubscribe();
  }

  private clearResourceSelectionWarningTimeout() {
    if (!this.resourceSelectionWarningTimeout) {
      return;
    }

    clearTimeout(this.resourceSelectionWarningTimeout);
    this.resourceSelectionWarningTimeout = null;
  }

  private handleCurrentResourceFilterChange(filter: IResourceFilterUpdateCrate | null | undefined) {
    const rangeKey = this.getNotificationRangeKey(filter?.start, filter?.end);
    if (this.currentNotificationRangeKey !== null && this.currentNotificationRangeKey !== rangeKey) {
      this.closeResourceSelectionWarning();
      this.cancelResourceRemovalConfirmationNotifications();
    }

    this.currentNotificationRangeKey = rangeKey;
  }

  private cancelResourceRemovalConfirmationNotifications() {
    this.resourceRemovalNotifications()
      .filter((notification) => notification.confirmation)
      .map((notification) => notification.id)
      .forEach((notificationId) => this.cancelResourceRemovalNotification(notificationId));
  }

  private getNotificationRangeKey(start: unknown, end: unknown): string {
    return [this.getNotificationDateKey(start), this.getNotificationDateKey(end)].join('|');
  }

  private getNotificationDateKey(value: unknown): string {
    const date = value instanceof Date ? value : new Date(value as string);

    return Number.isFinite(date.getTime()) ? date.toISOString() : '';
  }
}
